/**
 * Cable Offer — Custom elements for the cart drawer cable offer.
 *
 * CableVariantSelects: handles variant option radios and cascading availability.
 * CableOffer: manages tabs, pricing, add-to-cart, and cart refresh.
 */

/* ─── CableVariantSelects ─── */

class CableVariantSelects extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.currentVariant = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.product = this.dataset.product;
    this.fieldsets = Array.from(this.querySelectorAll('fieldset'));

    this.getSelectedOptions();
    this.updateCurrentVariant();

    this.fieldsets.forEach((fieldset, index) => {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.getSelectedOptions();
          this.updateAllVariantOptions(index + 1);
          this.updateCurrentVariant();
          this.updateFieldsetLabel(input);
          this.dispatchEvent(new CustomEvent('cable-variant-change', {
            bubbles: true,
            detail: { variant: this.currentVariant, productId: this.product }
          }));
        });
      });
    });
  }

  getSelectedOptions() {
    this.options = this.fieldsets.map((fieldset) => {
      const checked = Array.from(fieldset.querySelectorAll('input')).find((r) => r.checked);
      return checked ? checked.value : null;
    });
  }

  updateCurrentVariant() {
    const variants = this.getVariantData();
    this.currentVariant = variants.find((v) =>
      v.options.every((opt, i) => opt === this.options[i])
    ) || null;
  }

  updateFieldsetLabel(target) {
    const label = target.closest('fieldset').querySelector('[data-selected-value]');
    if (label) label.textContent = target.value;
  }

  getVariantData() {
    if (!this._variantData) {
      const script = this.querySelector('script[type="application/json"]');
      this._variantData = script ? JSON.parse(script.textContent) : [];
    }
    return this._variantData;
  }

  updateFieldsetOptions(index, variants) {
    const fieldset = this.fieldsets[index];
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const currentChecked = fieldset.querySelector('input:checked');
    const currentValue = currentChecked ? currentChecked.value : null;

    inputs.forEach((input) => {
      const isAvailable = variants.some((v) => v.options[index] === input.value);
      input.disabled = !isAvailable;
      const label = input.nextElementSibling;
      if (label) {
        label.classList.toggle('cable-offer__radio-label--disabled', !isAvailable);
      }
    });

    const stillValid = variants.some((v) => v.options[index] === currentValue);
    if (!stillValid) {
      const first = inputs.find((i) => !i.disabled);
      if (first) {
        first.checked = true;
        this.updateFieldsetLabel(first);
        return first.value;
      }
      return null;
    }
    return currentValue;
  }

  updateAllVariantOptions(startIndex = 0) {
    let variants = this.getVariantData();

    for (let i = 0; i < startIndex; i++) {
      if (this.options[i] != null) {
        variants = variants.filter((v) => v.options[i] === this.options[i] && v.available);
      }
    }

    for (let i = startIndex; i < this.fieldsets.length; i++) {
      const selected = this.updateFieldsetOptions(i, variants);
      this.options[i] = selected;
      variants = variants.filter((v) => v.options[i] === selected);
    }
  }

  getSelectedVariantId() {
    this.getSelectedOptions();
    this.updateCurrentVariant();
    return this.currentVariant ? this.currentVariant.id : null;
  }
}

customElements.define('cable-variant-selects', CableVariantSelects);


/* ─── CableOffer ─── */

class CableOffer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.tier = this.dataset.tier;
    this.nonCableSubtotal = parseInt(this.dataset.nonCableSubtotal || '0', 10);
    this.hasCable = this.dataset.hasCable === 'true';
    this.cableInCartVariant = this.dataset.cableInCartVariant || null;

    this.bindTabs();
    this.bindAddButton();
    this.bindVariantChanges();
  }

  /* ─ Tab switching ─ */
  bindTabs() {
    const tabs = this.querySelectorAll('.cable-offer__tab');
    const panels = this.querySelectorAll('.cable-offer__panel');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          t.classList.remove('cable-offer__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('cable-offer__tab--active');
        tab.setAttribute('aria-selected', 'true');

        const handle = tab.dataset.productHandle;
        panels.forEach((panel) => {
          const isActive = panel.dataset.panel === handle;
          panel.classList.toggle('cable-offer__panel--active', isActive);
          panel.hidden = !isActive;
        });
      });
    });
  }

  /* ─ Variant change listener ─ */
  bindVariantChanges() {
    this.addEventListener('cable-variant-change', (e) => {
      const { variant } = e.detail;
      if (!variant) return;
      const panel = e.target.closest('.cable-offer__panel');
      if (panel) this.updatePriceDisplay(panel, variant);
    });
  }

  updatePriceDisplay(panel, variant) {
    const priceEl = panel.querySelector('[data-price-display]');
    if (!priceEl) return;

    const price = variant.price;
    const formatted = this.formatMoney(price);

    // Build price display using DOM methods (safe — no user input)
    priceEl.textContent = '';

    const strikeEl = document.createElement('s');
    strikeEl.className = 'cable-offer__original-price';
    strikeEl.textContent = formatted;
    priceEl.appendChild(strikeEl);

    priceEl.appendChild(document.createTextNode(' '));

    if (this.tier === 'free') {
      const freeEl = document.createElement('span');
      freeEl.className = 'cable-offer__discount-price cable-offer__discount-price--free';
      freeEl.textContent = 'FREE';
      priceEl.appendChild(freeEl);
    } else {
      const halfFormatted = this.formatMoney(Math.round(price / 2));
      const halfEl = document.createElement('span');
      halfEl.className = 'cable-offer__discount-price';
      halfEl.textContent = halfFormatted;
      priceEl.appendChild(halfEl);

      priceEl.appendChild(document.createTextNode(' '));

      const badgeEl = document.createElement('span');
      badgeEl.className = 'cable-offer__discount-badge';
      badgeEl.textContent = '50% OFF';
      priceEl.appendChild(badgeEl);
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ─ Add to cart ─ */
  bindAddButton() {
    const addButton = this.querySelector('[data-cable-add-button]');
    if (!addButton) return;
    addButton.addEventListener('click', () => this.addCableToCart());
  }

  getActiveVariantSelector() {
    const activePanel = this.querySelector('.cable-offer__panel--active');
    return activePanel ? activePanel.querySelector('cable-variant-selects') : null;
  }

  async addCableToCart() {
    const selector = this.getActiveVariantSelector();
    if (!selector) return;

    const variantId = selector.getSelectedVariantId();
    if (!variantId) return;

    this.setLoading(true);

    try {
      // If there's already a cable in cart, remove it first
      if (this.hasCable && this.cableInCartVariant) {
        await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.cableInCartVariant, quantity: 0 })
        });
      }

      // Add the selected cable
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      // Re-render cart drawer using Dawn theme's section rendering pattern
      await this.refreshCartDrawer();

      // Notify Extend integration
      document.dispatchEvent(new Event('extend.cart-updated'));

    } catch (err) {
      console.error('Cable offer add-to-cart error:', err);
      this.setLoading(false);
    }
    // No finally { setLoading(false) } — refreshCartDrawer replaces this element
    // entirely with fresh server-rendered HTML, so the new element starts clean.
  }

  setLoading(loading) {
    const addButton = this.querySelector('[data-cable-add-button]');
    const addButtonText = this.querySelector('.cable-offer__add-button-text');
    const spinner = this.querySelector('.cable-offer__spinner');
    if (addButton) {
      addButton.disabled = loading;
      addButton.classList.toggle('cable-offer__add-button--loading', loading);
    }
    if (addButtonText) addButtonText.classList.toggle('hidden', loading);
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  async refreshCartDrawer() {
    // Uses the same DOM update pattern as CartDrawer.renderContents() in cart-drawer.js.
    // The temp element + appendChild approach ensures custom elements are upgraded
    // AFTER their children are in place (unlike innerHTML which upgrades during parsing
    // before children exist, causing empty querySelectorAll results).
    try {
      const response = await fetch(routes.cart_url);
      if (!response.ok) throw new Error('Cart fetch failed');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Replace #CartDrawer content using DOM node moving (safe: server-rendered HTML)
      const newContent = parsed.querySelector('#CartDrawer');
      const currentDrawer = document.getElementById('CartDrawer');
      if (newContent && currentDrawer) {
        currentDrawer.textContent = '';
        const temp = document.createElement('div');
        temp.insertAdjacentHTML('afterbegin', newContent.innerHTML);
        while (temp.firstChild) currentDrawer.appendChild(temp.firstChild);
      }

      // Update cart icon bubble (safe: server-rendered HTML from same origin)
      const newBubble = parsed.querySelector('#cart-icon-bubble');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.textContent = '';
        const temp2 = document.createElement('div');
        temp2.insertAdjacentHTML('afterbegin', newBubble.innerHTML);
        while (temp2.firstChild) currentBubble.appendChild(temp2.firstChild);
      }

      // Re-bind overlay close handler (same as CartDrawer.renderContents)
      const overlay = document.querySelector('#CartDrawer-Overlay');
      const drawer = document.querySelector('cart-drawer');
      if (overlay && drawer) {
        overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    } catch (err) {
      console.error('Cart drawer refresh error:', err);
    }
  }

}

customElements.define('cable-offer', CableOffer);
